/** @format */

import React, { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import { _$, Button, Message, Divider } from '@king-design/react'
import { useParams } from 'react-router'
import { getRecordDetail, getRecordProcess, upgradeWorkflow } from 'src/api/ApiEventOpsRecord'
import { getScene } from 'src/api/ApiEventOpsScene'
import { useAsyncFn } from 'react-use'
import { get } from 'lodash'
import { UserInfo, CustomPageContainer, IconButton } from 'src/components'
import { StatusEnum, getEvaluateInfo } from '../container'
import { handleRouterData, handleNodeData } from '../../Scene/container'
import BaseInfo from './BaseInfo'
import FlowProcess from './FlowProcess'
import LogDetail from './LogDetail'
import Evalate from './Evalate'
import RemarkDialog, { RemarkDialogAction } from './RemarkDialgo'

const TriggerPage: React.FC<{ className?: string }> = ({ className }) => {
  const params = useParams()
  const ID = get(params, 'ID')
  const [remarkDialog, setRemarkDialog] = useState<{
    visible: boolean
    type: RemarkDialogAction
  }>({ visible: false, type: 'approve' })

  const [{ value: detail }, fetchDetail] = useAsyncFn(async () => {
    // 基础信息
    const baseInfo = await getRecordDetail(ID)
    // 场景信息
    const sceneInfo = await getScene(baseInfo?.SceneID)
    // 执行进程
    const processInfo = await getRecordProcess(ID)
    // 查找当前进行中的节点
    let nowNode = null
    for (let i = processInfo?.Nodes.length - 1; i >= 0; i--) {
      const node = processInfo?.Nodes[i]
      if (node.IsActive === 1 && baseInfo?.Status === 2) {
        nowNode = node
        break
      }
    }
    // 评价信息
    const evaluateInfo = getEvaluateInfo(
      sceneInfo?.Workflow.Nodes,
      baseInfo.ResultList.filter(item => item.Evaluation !== -2)
    )
    // 执行记录流程图 - 获取最后一个节点
    const nodes = sceneInfo?.Workflow.Nodes.map(node => {
      const findProces = processInfo?.Nodes.find(proces => node.Sign === proces.Sign)
      return {
        ...node,
        IsActive: findProces?.IsActive || 3,
        ProcesDetails: findProces,
        CreatedAt: findProces?.CreatedAt,
        FinishedAt: findProces?.FinishedAt,
        ProcessID: findProces?.ID
      }
    })
    const routers = sceneInfo?.Workflow?.Routes.map(route => {
      const findProces = processInfo?.Routes.find(proces => route.Sign === proces.Sign)
      return {
        ...route,
        IsActive: findProces ? 2 : 3
      }
    })
    // 流程图数据
    const flowInfo = {
      nodes: handleNodeData(nodes),
      routers: handleRouterData(routers)
    }
    return { baseInfo, sceneInfo, processInfo, nowNode, evaluateInfo, flowInfo }
  }, [ID])

  // 初始化
  useEffect(() => {
    fetchDetail()
  }, [ID])

  const { baseInfo, processInfo, nowNode, evaluateInfo, flowInfo } = detail || {}

  // 轮询定时器 ref
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null)
  // 保存最新的 fetchDetail 函数引用
  const fetchDetailRef = useRef(fetchDetail)
  fetchDetailRef.current = fetchDetail

  // 当 baseInfo?.Status === 2 时，每10秒自动刷新数据
  useEffect(() => {
    // 清除之前的定时器
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current)
      pollingTimerRef.current = null
    }

    // 如果状态是 2（进行中），启动轮询
    if (baseInfo?.Status === 2) {
      pollingTimerRef.current = setInterval(() => {
        fetchDetailRef.current()
      }, 10000) // 10秒 = 10000毫秒
    }

    // 清理函数：组件卸载或状态改变时清除定时器
    return () => {
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current)
        pollingTimerRef.current = null
      }
    }
  }, [baseInfo?.Status])

  // 催办
  const [, fetchUpgrade] = useAsyncFn(async () => {
    const result = await upgradeWorkflow({
      SceneRecordID: baseInfo?.ID,
      WfProcessID: baseInfo?.WfProcessID,
      WfProcessNodeID: nowNode?.ID
    })
    if (result) {
      Message.success(_$('催办成功'))
    }
  }, [baseInfo?.ID, baseInfo?.WfProcessID, nowNode?.ID])
  return (
    <div id="record_detail" className={className}>
      <CustomPageContainer
        title={baseInfo?.Name}
        showBack
        extra={
          <div className="header_extra ksc-space">
            {nowNode ? (
              <>
                <p>
                  {_$('当前节点')}:{nowNode?.Name}
                </p>
                {nowNode?.Type === 'USERTASK' && nowNode?.Participants?.length ? (
                  <>
                    <Divider />
                    <div>
                      {_$('当前处理人')}:
                      {nowNode?.Participants?.map((item: string, index: number) => (
                        <>
                          <UserInfo name={item} key={item} />
                          {index !== nowNode?.Participants.length - 1 && '、'}
                        </>
                      ))}
                    </div>

                    <IconButton
                      title={_$('催办')}
                      icon="k-icon-notification-fill"
                      onClick={() => {
                        fetchUpgrade()
                      }}
                    />
                  </>
                ) : null}
              </>
            ) : null}
            {/* 按钮：通过（人工节点） */}
            {nowNode?.Type === 'USERTASK' ? (
              <Button
                type="primary"
                onClick={() => {
                  setRemarkDialog({ visible: true, type: 'approve' })
                }}
              >
                {_$('通过')}
              </Button>
            ) : null}
            {/* 按钮：终止流程 */}
            {baseInfo?.Status === 2 ? (
              <Button
                onClick={() => {
                  setRemarkDialog({ visible: true, type: 'stop' })
                }}
              >
                {_$('终止')}
              </Button>
            ) : null}

            <RemarkDialog
              {...remarkDialog}
              params={{
                SceneRecordID: baseInfo?.ID,
                WfProcessID: baseInfo?.WfProcessID,
                WfProcessNodeID: nowNode?.ID
              }}
              onClose={(update: boolean) => {
                if (update) {
                  fetchDetail()
                }
                setRemarkDialog({ visible: false, type: 'approve' })
              }}
            />
            {/* 按钮：评价 */}
            {baseInfo?.Status === 3 && baseInfo?.Type === 'published' && (
              <Evalate
                baseInfo={baseInfo}
                updateRecord={fetchDetail}
                evaluation={baseInfo.Evaluation === 1}
                evaluateInfo={evaluateInfo}
              />
            )}

            {/* 按钮：刷新 */}
            <IconButton
              type="refresh"
              title={_$('刷新')}
              hoverBackground
              onClick={() => fetchDetail()}
            />
          </div>
        }
      >
        {baseInfo ? (
          <div className={`status_badge status_badge_${baseInfo?.Status}`}>
            {baseInfo?.Status === 3 ? _$('结束') : StatusEnum[baseInfo?.Status]}
          </div>
        ) : null}
        {baseInfo ? <BaseInfo baseInfo={baseInfo} /> : null}
        {processInfo ? (
          <FlowProcess
            nodes={flowInfo?.nodes || []}
            edges={flowInfo?.routers || []}
            variables={processInfo?.Variables || []}
          />
        ) : null}
        {processInfo ? (
          <LogDetail
            WfProcessID={baseInfo?.WfProcessID}
            baseInfo={baseInfo}
            logs={processInfo?.Logs}
            updateRecord={fetchDetail}
          />
        ) : null}
      </CustomPageContainer>
    </div>
  )
}

export default styled(TriggerPage)`
  .status_badge {
    position: absolute;
    top: 160px;
    right: 40px;
    font-size: 24px;
    transform: rotateZ(330deg);
    width: 100px;
    height: 100px;
    line-height: 100px;
    text-align: center;
    border-radius: 50%;
    animation: mymove 1s 1;
    -webkit-animation: mymove 1s 1;
    &.status_badge_-1 {
      color: #faad14;
      border: 3px dashed #faad14;
    }
    &.status_badge_2 {
      color: #0091ea;
      border: 3px dashed #0091ea;
    }
    &.status_badge_3 {
      color: #36b342;
      border: 3px dashed #36b342;
    }
  }
  @keyframes mymove {
    0% {
      transform: rotateZ(330deg) scale(7); /*开始为原始大小*/
    }
    25% {
      transform: rotateZ(330deg) scale(1); /*放大1.1倍*/
    }
    50% {
      transform: rotateZ(330deg) scale(1);
    }
    75% {
      transform: rotateZ(330deg) scale(1);
    }
  }
  .header_extra {
    margin-top: 10px;
    p {
      margin-bottom: 0;
    }
  }
  .k-card {
    margin-top: 16px;
    .k-card-header {
      height: 38px;
      font-size: 12px;
    }
  }
`
